import { FileSearch, Settings2 } from 'lucide-react';
import ClienteCombobox from '@/components/ui/ClienteCombobox.jsx';
import SearchableSelect from './SearchableSelect.jsx';
import { buttonClass, fieldClass, LabeledField, primaryButtonClass, shellCard } from './shared.jsx';
import { SIMPLE_CALCULATION_OPTIONS } from './ruleUx.js';

function Pill({ label, value, tone = 'default' }) {
  const toneClass = tone === 'brand'
    ? 'border-[rgba(var(--brand),0.24)] bg-[rgba(var(--brand),0.08)] text-[rgb(var(--brand))]'
    : 'border-[rgba(var(--border),0.35)] bg-[rgba(var(--panel),0.45)] text-[rgb(var(--fg))]';
  return (
    <div className={`rounded-2xl border px-3 py-2 ${toneClass}`}>
      <p className="text-[10px] font-semibold uppercase tracking-[0.14em] opacity-80">{label}</p>
      <p className="mt-1 text-sm font-semibold">{value}</p>
    </div>
  );
}

export default function SimpleRuleForm({
  form,
  concepts,
  selectedCliente,
  onClienteSelect,
  onChange,
  onSubmit,
  onToggleMode,
  onTryRule,
  simpleCalculationType,
  onSimpleCalculationTypeChange,
  scopeLabel,
  priorityLabel,
  summaryText,
  activeVersionLabel,
  serviceOptions,
  locationOptions,
  errors,
  isSubmitting = false,
  canTryRule = false,
  lastSavedSummary = '',
  assistantPanel = null,
}) {
  return (
    <section className="space-y-4">
      <header className={`${shellCard} p-4`}>
        <div className="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
          <div>
            <p className="text-xs font-semibold uppercase tracking-[0.16em] text-[rgba(var(--muted),0.95)]">Modo simple</p>
            <h2 className="mt-1 text-xl font-semibold text-[rgb(var(--fg))]">Crear una regla funcional rapido</h2>
            <p className="mt-2 text-sm text-[rgba(var(--fg),0.82)]">
              Carga solo que calcula, cuanto vale y cuando aplica. El resto queda disponible en modo avanzado.
            </p>
          </div>
          <div className="flex flex-wrap gap-2">
            {canTryRule ? (
              <button type="button" className={buttonClass} onClick={onTryRule}>
                <FileSearch className="mr-2 h-4 w-4" />Probar esta regla
              </button>
            ) : null}
            <button type="button" className={buttonClass} onClick={onToggleMode}>
              <Settings2 className="mr-2 h-4 w-4" />Modo avanzado
            </button>
          </div>
        </div>
      </header>

      <div className="grid gap-4 xl:grid-cols-[minmax(0,1.5fr)_minmax(320px,0.9fr)]">
        <div className="space-y-4">
          <section className={`${shellCard} p-4`}>
            <h3 className="text-base font-semibold text-[rgb(var(--fg))]">Que calcula</h3>
            <div className="mt-4 grid grid-cols-12 gap-4">
              <LabeledField label="Concepto" hint="Obligatorio." span="col-span-12 md:col-span-6">
                <SearchableSelect
                  value={form.conceptId || ''}
                  onChange={onChange.bind(null, 'conceptId')}
                  options={concepts.map((item) => ({ value: item.id, label: `${item.codigo} · ${item.nombre}` }))}
                  placeholder="Seleccionar concepto"
                  searchPlaceholder="Buscar concepto"
                  emptyMessage="Sin conceptos"
                />
                {errors.conceptId ? <span className="mt-1 block text-xs text-[rgb(var(--danger))]">{errors.conceptId}</span> : null}
              </LabeledField>

              <LabeledField label="Tipo de calculo" hint="Obligatorio." span="col-span-12 md:col-span-6">
                <SearchableSelect
                  value={simpleCalculationType}
                  onChange={onSimpleCalculationTypeChange}
                  options={SIMPLE_CALCULATION_OPTIONS.map((item) => ({ value: item.value, label: item.label }))}
                  placeholder="Seleccionar calculo"
                  searchPlaceholder="Buscar calculo"
                  emptyMessage="Sin opciones"
                />
                {errors.metodoCalculo ? <span className="mt-1 block text-xs text-[rgb(var(--danger))]">{errors.metodoCalculo}</span> : null}
              </LabeledField>

              <LabeledField label="Valor" hint={simpleCalculationType === 'percentage' ? 'Porcentaje.' : 'Importe en ARS.'} span="col-span-12 md:col-span-6">
                <input
                  type="number"
                  className={fieldClass}
                  value={simpleCalculationType === 'percentage' ? form.porcentaje : form.valorUnitario}
                  onChange={(event) => onChange(simpleCalculationType === 'percentage' ? 'porcentaje' : 'valorUnitario', event.target.value)}
                  placeholder={simpleCalculationType === 'percentage' ? '12' : '1500'}
                />
                {errors.value ? <span className="mt-1 block text-xs text-[rgb(var(--danger))]">{errors.value}</span> : null}
              </LabeledField>

              <LabeledField label="Prioridad" hint="Editable; por defecto sigue el alcance." span="col-span-12 md:col-span-6">
                <input type="number" className={fieldClass} value={form.prioridad} onChange={(event) => onChange('prioridad', event.target.value)} />
              </LabeledField>
            </div>
          </section>

          <section className={`${shellCard} p-4`}>
            <h3 className="text-base font-semibold text-[rgb(var(--fg))]">Cuando aplica</h3>
            <div className="mt-4 grid grid-cols-12 gap-4">
              <LabeledField label="Cliente" hint="Opcional." span="col-span-12 md:col-span-6">
                <div className="mt-1">
                  <ClienteCombobox
                    id="simple-rule-cliente"
                    name="clienteId"
                    value={form.clienteId || ''}
                    selectedCliente={selectedCliente}
                    onChange={(event) => onChange('clienteId', event?.target?.value || '')}
                    onClienteSelect={onClienteSelect}
                    placeholder="Buscar cliente"
                  />
                </div>
              </LabeledField>

              <LabeledField label="Servicio" hint="Opcional." span="col-span-12 md:col-span-6">
                <SearchableSelect
                  value={form.servicioCodigo || ''}
                  onChange={onChange.bind(null, 'servicioCodigo')}
                  options={[{ value: '', label: 'Todos los servicios' }, ...serviceOptions]}
                  placeholder="Todos los servicios"
                  searchPlaceholder="Buscar servicio"
                  emptyMessage="Sin servicios"
                />
              </LabeledField>

              <LabeledField label="Origen" hint="Opcional." span="col-span-12 md:col-span-6">
                <SearchableSelect
                  value={form.origenLocalidadId || ''}
                  onChange={onChange.bind(null, 'origenLocalidadId')}
                  options={[{ value: '', label: 'Cualquier origen' }, ...locationOptions]}
                  placeholder="Cualquier origen"
                  searchPlaceholder="Buscar origen"
                  emptyMessage="Sin localidades"
                />
              </LabeledField>

              <LabeledField label="Destino" hint="Opcional." span="col-span-12 md:col-span-6">
                <SearchableSelect
                  value={form.destinoLocalidadId || ''}
                  onChange={onChange.bind(null, 'destinoLocalidadId')}
                  options={[{ value: '', label: 'Cualquier destino' }, ...locationOptions]}
                  placeholder="Cualquier destino"
                  searchPlaceholder="Buscar destino"
                  emptyMessage="Sin localidades"
                />
              </LabeledField>
            </div>

            <div className="mt-5 flex flex-wrap gap-2">
              <button type="button" className={primaryButtonClass} onClick={onSubmit} disabled={isSubmitting}>
                {isSubmitting ? 'Guardando...' : 'Guardar regla'}
              </button>
            </div>
          </section>
        </div>

        <aside className="space-y-4">
          <section className={`${shellCard} p-4`}>
            <p className="text-xs font-semibold uppercase tracking-[0.16em] text-[rgba(var(--muted),0.95)]">Resumen de regla</p>
            <p className="mt-3 rounded-2xl border border-[rgba(var(--brand),0.24)] bg-[rgba(var(--brand),0.08)] p-4 text-sm leading-6 text-[rgb(var(--fg))]">
              {summaryText}
            </p>
          </section>

          <section className={`${shellCard} grid gap-3 p-4`}>
            <Pill label="Alcance" value={scopeLabel} tone="brand" />
            <Pill label="Prioridad" value={priorityLabel} />
            <Pill label="Version activa" value={activeVersionLabel || 'Sin version'} />
          </section>

          {lastSavedSummary ? (
            <section className={`${shellCard} p-4`}>
              <p className="text-xs font-semibold uppercase tracking-[0.16em] text-[rgba(var(--muted),0.95)]">Ultima regla guardada</p>
              <p className="mt-2 text-sm text-[rgba(var(--fg),0.84)]">{lastSavedSummary}</p>
            </section>
          ) : null}

          {assistantPanel}
        </aside>
      </div>
    </section>
  );
}
